import React, { useMemo, useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
  Label,
  Input,
  Badge,
  UncontrolledTooltip,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom"; // Add useNavigate
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import TableContainer from "../../components/Common/TableContainer";
import { getAllTicket, deleteTicket } from "helpers/requests"; // Remove answerTicket
import { Oval } from "react-loader-spinner";

const Support = (props) => {
  const navigate = useNavigate(); // Add useNavigate

  useEffect(() => {
    document.title = "Support Requests";
  }, []);

  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);

  // Enhanced filtering states
  const [statusFilter, setStatusFilter] = useState(() => localStorage.getItem("sup_status") || "all");
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem("sup_search") || "");
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("sup_sort") || "newest");

  useEffect(() => {
    localStorage.setItem("sup_status", statusFilter);
    localStorage.setItem("sup_search", searchTerm);
    localStorage.setItem("sup_sort", sortBy);
  }, [statusFilter, searchTerm, sortBy]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const tickets = await getAllTicket();
        setRequests(tickets);
        setFilteredRequests(tickets);
      } catch (error) {
        console.error("Error fetching tickets:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTickets();
  }, []);

  // Advanced filtering effect
  useEffect(() => {
    let filtered = [...requests];

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((request) => request.state === statusFilter);
    }

    // Search filter
    if (searchTerm) {
      const searchTermLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (request) =>
          request.subject.toLowerCase().includes(searchTermLower) ||
          request.description.toLowerCase().includes(searchTermLower) ||
          (request.adminUsername &&
            request.adminUsername.toLowerCase().includes(searchTermLower))
      );
    }

    // Sorting
    if (sortBy === "newest") {
      filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (sortBy === "oldest") {
      filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }

    setFilteredRequests(filtered);
  }, [requests, statusFilter, searchTerm, sortBy]);

  const handleSelectAll = () => {
    if (isSelectAllChecked) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredRequests.map((request) => request.id));
    }
    setIsSelectAllChecked(!isSelectAllChecked);
  };

  const handleRowSelect = (id) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter((rowId) => rowId !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  const handleDelete = (request) => {
    if (window.confirm("Are you sure you want to delete this ticket?")) {
      deleteTicket(request.id)
        .then(() => {
          setRequests((prevRequests) =>
            prevRequests.filter((ticket) => ticket.id !== request.id)
          );
          setSelectedRows(selectedRows.filter((rowId) => rowId !== request.id));
        })
        .catch((error) => {
          console.error("Error deleting ticket:", error);
        });
    }
  };

  const handleCheckRequest = (request) => {
    // Redirect to the TicketDetails page with the ticket ID
   
    navigate(`/support-tickets/${request.id}`);
    
  };

  const columns = useMemo(
    () => [
      {
        header: () => (
          <FormGroup check className="font-size-16">
            <Label check>
              <Input
                type="checkbox"
                id="checkAll"
                checked={isSelectAllChecked}
                onChange={handleSelectAll}
              />
            </Label>
          </FormGroup>
        ),
        accessorKey: "id",
        cell: (cellProps) => (
          <FormGroup check className="font-size-16">
            <Label check>
              <Input
                type="checkbox"
                checked={selectedRows.includes(cellProps.row.original.id)}
                onChange={() => handleRowSelect(cellProps.row.original.id)}
              />
            </Label>
          </FormGroup>
        ),
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Subject",
        accessorKey: "subject",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Admin In Charge",
        accessorKey: "adminUsername",
        cell: (cellProps) =>
          cellProps.row.original.adminUsername || "Not assigned yet",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Created At",
        accessorKey: "created_at",
        cell: (cellProps) =>
          new Date(cellProps.row.original.created_at).toLocaleDateString(),
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Status",
        accessorKey: "state",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => {
          const stateMap = {
            OPEN: { text: "Open", color: "danger" },
            CLOSED: { text: "Closed", color: "success" },
          };
          const stateInfo = stateMap[cellProps.row.original.state] || {
            text: "Unknown",
            color: "secondary",
          };
          return (
            <Badge pill className={`badge-soft-${stateInfo.color} ms-1`}>
              {stateInfo.text}
            </Badge>
          );
        },
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => (
          <div className="d-flex gap-3">
<Link
  to={`/support-tickets/${cellProps.row.original.id}`}
  className="text-primary"
>
              <i
                className="mdi mdi-eye font-size-18"
                id={`checktooltip-${cellProps.row.original.id}`}
              />
              <UncontrolledTooltip
                placement="top"
                target={`checktooltip-${cellProps.row.original.id}`}
              >
                View Details
              </UncontrolledTooltip>
            </Link>
            <Link
              to="#"
              className="text-danger"
              onClick={() => handleDelete(cellProps.row.original)}
            >
              <i
                className="mdi mdi-delete font-size-18"
                id={`deletetooltip-${cellProps.row.original.id}`}
              />
              <UncontrolledTooltip
                placement="top"
                target={`deletetooltip-${cellProps.row.original.id}`}
              >
                Delete
              </UncontrolledTooltip>
            </Link>
          </div>
        ),
      },
    ],
    [selectedRows, isSelectAllChecked]
  );

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title={props.t("Support")}
          breadcrumbItem={props.t("Requests")}
        />
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <CardTitle className="mb-4">Support Requests</CardTitle>

                {/* Filtering and Sorting Row */}
                <Row className="mb-3">
                  <Col md="4">
                    <FormGroup>
                      <Label for="statusFilter">Filter by Status</Label>
                      <Input
                        type="select"
                        id="statusFilter"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <option value="all">All Statuses</option>
                        <option value="OPEN">Open</option>
                        <option value="CLOSED">Closed</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label for="sortBy">Sort By</Label>
                      <Input
                        type="select"
                        id="sortBy"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                      >
                        <option value="newest">Newest First</option>
                        <option value="oldest">Oldest First</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label for="searchInput">Search</Label>
                      <Input
                        type="text"
                        id="searchInput"
                        placeholder="Search by subject, description, admin"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {isLoading ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "300px",
                    }}
                  >
                    <Oval
                      height={80}
                      width={80}
                      color="#556ee6"
                      visible={true}
                      ariaLabel="oval-loading"
                      secondaryColor="#4fa94d"
                      strokeWidth={2}
                      strokeWidthSecondary={2}
                    />
                  </div>
                ) : (
                  <TableContainer
                    columns={columns}
                    data={filteredRequests}
                    isLoading={false}
                    isPagination={true}
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline"
                    theadClass="table-light"
                    pagination="pagination"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    title=""
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

Support.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(Support);